import { useCallback, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { invitePatient } from "~/api/patients";
import { errorToast, useToastStore } from "~/components";
import { Button, Input, Modal } from "~/ui";

interface InvitePatientModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function InvitePatientModal({
  isOpen,
  onClose,
}: InvitePatientModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { pushToast } = useToastStore();

  const passwordSchema = z
    .string()
    .min(8, {
      message:
        t("patients.password_min_length") ??
        "Password must be at least 8 characters",
    })
    .regex(/[a-z]/, {
      message:
        t("patients.password_lowercase") ??
        "Password must contain a lowercase letter",
    })
    .regex(/[A-Z]/, {
      message:
        t("patients.password_uppercase") ??
        "Password must contain an uppercase letter",
    })
    .regex(/[0-9]/, {
      message:
        t("patients.password_number") ?? "Password must contain a number",
    })
    .regex(/[^a-zA-Z0-9]/, {
      message:
        t("patients.password_special") ??
        "Password must contain a special character",
    });

  const schema = z.object({
    email: z.string({ required_error: t("patients.please_enter_email") }),
    name: z.string({ required_error: t("patients.name_required") }),
    surname: z.string({ required_error: t("patients.surname_required") }),
    password: passwordSchema,
    force_change_password: z.boolean().default(false),
  });

  // Generate a valid random password
  function generatePassword() {
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const special = "!@#$%^&*()_+-=[]{}|;:,.<>?";
    const all = lower + upper + numbers + special;
    const password = [
      lower[Math.floor(Math.random() * lower.length)],
      upper[Math.floor(Math.random() * upper.length)],
      numbers[Math.floor(Math.random() * numbers.length)],
      special[Math.floor(Math.random() * special.length)],
    ];
    for (let i = 4; i < 12; i++) {
      password.push(all[Math.floor(Math.random() * all.length)]);
    }
    return password.sort(() => 0.5 - Math.random()).join("");
  }

  type InvitePatientFormData = z.infer<typeof schema>;

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm<InvitePatientFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      force_change_password: false,
    },
    mode: "onChange",
  });

  const resetForm = useCallback(
    () =>
      reset({
        email: undefined,
        name: undefined,
        surname: undefined,
        password: undefined,
        force_change_password: false,
      }),
    [reset],
  );

  const closeAndReset = () => {
    resetForm();
    onClose();
  };

  const { mutate, isSuccess: isSubmitSuccessful } = useMutation({
    mutationFn: invitePatient,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getPatientInvitations"],
      });
      onClose();
      void pushToast({
        type: "success",
        message: t("patients.invite_success"),
      });
    },
    onError: errorToast,
  });

  useEffect(resetForm, [resetForm, isSubmitSuccessful]);

  const onSubmit = ({
    email,
    name,
    surname,
    password,
    force_change_password,
  }: InvitePatientFormData) => {
    mutate({
      email,
      name,
      surname,
      password,
      force_change_password,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      onClose={closeAndReset}
      title={t("patients.invite_patient")}
    >
      <form
        className="flex w-full flex-col gap-3"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name="email"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Input
              id="email"
              className="w-full"
              label={t("patients.email_address")}
              placeholder={t("patients.email")}
              error={errors.email?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />

        <Controller
          name="name"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Input
              id="name"
              className="w-full"
              label={t("patients.name")}
              placeholder={t("patients.enter_name")}
              error={errors.name?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />

        <Controller
          name="surname"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Input
              id="surname"
              className="w-full"
              label={t("patients.surname")}
              placeholder={t("patients.enter_surname")}
              error={errors.surname?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          render={({ field: { onChange, value } }) => (
            <div className="relative w-full">
              <Input
                id="password"
                type="text"
                className="w-full"
                label={t("login.password")}
                placeholder={t("login.enter_password")}
                value={value}
                onChange={onChange}
              />
              <button
                type="button"
                onClick={() =>
                  setValue("password", generatePassword(), {
                    shouldValidate: true,
                  })
                }
                className="absolute right-3 top-12 flex items-center justify-center text-xs text-primary-60 hover:underline"
              >
                {t("general.generate")}
              </button>
              {errors.password && (
                <div className="mt-1 text-left text-xs text-red-500">
                  {errors.password.message && (
                    <div>{errors.password.message}</div>
                  )}
                  {errors.password.types &&
                    Object.values(errors.password.types).map((msg, idx) => (
                      <div key={idx}>{msg}</div>
                    ))}
                </div>
              )}
            </div>
          )}
        />

        <Controller
          name="force_change_password"
          control={control}
          render={({ field: { onChange, value } }) => (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="force_change_password"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 bg-transparent text-primary-60 focus:ring-primary-60 dark:border-neutral-variant-80"
              />
              <label
                htmlFor="force_change_password"
                className="text-sm font-medium text-secondary-30 dark:text-secondary"
              >
                {t("patients.force_change_password")}
              </label>
            </div>
          )}
        />

        <div className="mt-5 flex flex-row gap-3">
          <Button
            variant="outline"
            onClick={closeAndReset}
            text={t("general.cancel")}
            className="flex grow"
          />
          <Button
            type="submit"
            variant="filled"
            text={t("general.invite")}
            className="flex grow"
            disabled={!isValid}
          />
        </div>
      </form>
    </Modal>
  );
}
